import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SvcAudioToTextButtonService } from './svc-audio-to-text-button.service';
import { finalize, tap } from 'rxjs/operators';

enum AudioRecordingStep {
  TO_RECORD = 'TO_RECORD',
  RECORDING = 'RECORDING',
}
export enum ActionType {
  RECORD_AUDIO,
  AUDIO_TO_TEXT,
}

export interface SvcAudioToTextEvent {
  file: Blob;
}

@Component({
  selector: 'svc-audio-to-text-button',
  templateUrl: './svc-audio-to-text-button.component.html',
  styleUrls: ['./svc-audio-to-text-button.component.scss'],
  providers: [SvcAudioToTextButtonService],
})
export class SvcAudioToTextButtonComponent implements OnInit {
  @Input()
  public small: boolean = true;
  @Input()
  public disabled: boolean = false;
  @Input()
  public canChooseRecording: boolean = false;
  @Input()
  public shadow: boolean = false;
  @Input()
  public isThemeSolvace: boolean = false;

  public ActionType = ActionType;
  public loading: boolean = false;
  public chosenAction: ActionType;
  public step: AudioRecordingStep | string = AudioRecordingStep.TO_RECORD;
  public audioBlobs: Blob[] = [];
  public audioEnabled: boolean = false;
  public mediaRecorder: MediaRecorder;
  public mediaStream: MediaStream;

  public get isRecording(): boolean {
    return this.step === AudioRecordingStep.RECORDING;
  }

  @Output()
  public onText = new EventEmitter<string>();
  @Output()
  public onAudioRecorded = new EventEmitter<SvcAudioToTextEvent>();

  constructor(
    private service: SvcAudioToTextButtonService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  protected onClick(): void {
    if (this.loading || this.disabled) return;
    if (this.isRecording) {
      this.recordAudio();
    } else if (!this.canChooseRecording) {
      this.recordAudio(ActionType.AUDIO_TO_TEXT);
    }
  }

  public recordAudio(type?: ActionType): void {
    this.chosenAction = type ?? this.chosenAction;
    switch (this.step) {
      case AudioRecordingStep.TO_RECORD:
        this._startRecording();
        break;
      case AudioRecordingStep.RECORDING:
        this._stopRecording();
        break;
    }
  }

  private async _startRecording(): Promise<void> {
    try {
      await this._enableAudioUserMedia();
      this.step = AudioRecordingStep.RECORDING;
      this.audioBlobs = [];
      this.mediaRecorder.addEventListener('dataavailable', (event) => {
        this.audioBlobs.push(event.data);
      });
      this.mediaRecorder.start();
      // AB#69789 — the state flips after the `getUserMedia` promise resolves, i.e. outside any
      // template event binding. Nothing marks this view dirty, so under an OnPush host (the CIL
      // Comments modal) the button kept rendering the microphone while recording was already
      // running, and `matMenuTriggerFor` kept its stale non-null value.
      this._cdr.markForCheck();
    } catch {
      // AB#69789 — the user denied the mic (or no device is available). `_enableAudioUserMedia`
      // already reported it; roll the button back to its idle state instead of leaving it half
      // started, and mark the view so the rollback reaches an OnPush host.
      this.step = AudioRecordingStep.TO_RECORD;
      this.loading = false;
      this._cdr.markForCheck();
    }
  }

  private _stopRecording(): void {
    this.loading = true;
    this.mediaRecorder.addEventListener('stop', async () => {
      this.step = AudioRecordingStep.TO_RECORD;
      if (this.chosenAction === ActionType.RECORD_AUDIO) {
        this.loading = false;
        const blob = new Blob(this.audioBlobs, { type: 'audio/mp3' });
        this.onAudioRecorded.emit({ file: blob });
      } else {
        const blob = new Blob(this.audioBlobs, { type: 'audio/linear16' });
        this.service
          .transcript(blob)
          .pipe(
            tap((text: string) => {
              this.onText.emit(text);
            }),
            finalize(() => {
              this.loading = false;
              this._cdr.markForCheck();
            })
          )
          .subscribe();
      }
      // AB#69789 — `MediaRecorder` events also fire outside any template binding, so the
      // stop -> microphone transition (and the recorded attachment) only reached the screen
      // on the user's next click. Mark the view so the state lands as soon as it changes.
      this._cdr.markForCheck();
    });

    this.mediaRecorder.stop();
    this.mediaStream.getTracks().forEach((track) => track.stop());
  }

  private _enableAudioUserMedia(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          this.mediaStream = stream;
          this.mediaRecorder = new MediaRecorder(stream);
          resolve();
        })
        .catch((error) => {
          console.error('[svc-audio-to-text-button] microphone unavailable', error);
          reject(error);
        });
    });
  }
}
